import {AsyncPipe, DatePipe} from '@angular/common';
import {Component, OnDestroy, OnInit, inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {ICustomerRequest} from '@app/accounts/models/customer-request.model';
import {ICustomersState} from '@app/accounts/models/customer-state.model';
import {customersAction} from '@app/accounts/states/accounts.actions';
import {selectCustomers} from '@app/accounts/states/accounts.selector';
import {OrderStatusEnum} from '@app/checkout/enums/order-status.enum';
import {ICustomerOrganization} from '@app/checkout/models/customer-organization.model';
import {IOrdersDetails} from '@app/checkout/models/order-details.model';
import {Order} from '@app/checkout/models/order-request.model';
import {resetOrderFormDataFromMyOrdersPage} from '@app/checkout/state/checkout.actions';
import {IMyOrderRequestModel} from '@app/order/models/my-order-request.model';
import {DateTimeFormat, OptionAll} from '@app/shared/constants';
import {EActionStatus} from '@app/shared/enums/action-status.enum';
import {ECustomerOrganizationStatus} from '@app/shared/enums/header.enum';
import {loadOrdersOnOrganizationChange} from '@app/shared/state/header/header.action';
import {
  selectCustomerOrganization,
  selectCustomerOrganizationId,
  selectCustomerOrganizationStatus,
  selectCustomerRole,
} from '@app/shared/state/header/header.selector';
import {environment} from '@environments/environment';
import {Store} from '@ngrx/store';
import {TranslatePipe} from '@ngx-translate/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {PrimeTemplate, SelectItem} from 'primeng/api';
import {AutoComplete, AutoCompleteCompleteEvent} from 'primeng/autocomplete';
import {Bind} from 'primeng/bind';
import {Button} from 'primeng/button';
import {DatePicker} from 'primeng/datepicker';
import {InputText} from 'primeng/inputtext';
import {Message} from 'primeng/message';
import {Select} from 'primeng/select';
import {TableLazyLoadEvent, TableModule} from 'primeng/table';
import {Tag} from 'primeng/tag';
import {Tooltip} from 'primeng/tooltip';
import {Observable, Subject, takeUntil, tap} from 'rxjs';
import {EOrderStatus} from '../enums/order-status.enum';
import {ESortOption} from '../enums/sort-option.enum';
import {loadListOfOrders, loadOrderStatus} from '../state/my-order.actions';
import {selectListOfOrderStatus, selectOrderDetails, selectOrderDetailsStatus} from '../state/my-order.selector';

dayjs.extend(utc);

@Component({
  selector: 'app-my-orders',
  templateUrl: './my-orders.component.html',
  styleUrls: ['./my-orders.component.scss'],
  imports: [
    Bind,
    Button,
    FormsModule,
    InputText,
    AutoComplete,
    Select,
    DatePicker,
    Tooltip,
    TableModule,
    PrimeTemplate,
    Tag,
    Message,
    AsyncPipe,
    DatePipe,
    TranslatePipe,
  ],
})
export class MyOrdersComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  totalPages = 0;
  organizationId: number | null = null;
  patientNameInput!: string;
  orderIdInput!: number | null;
  dateRangeModel!: [Date, Date | null] | null;
  orderStatusModel!: number | null;
  patientNameModel!: string;
  orderIdModel!: number | null;
  organizations = [] as ICustomerOrganization[];
  firstIndex = 0;
  accountStatus$!: Observable<ECustomerOrganizationStatus>;
  accountStatus!: ECustomerOrganizationStatus;
  listOfOrderStatus!: SelectItem[];
  myOrderDetailsStatus$: Observable<EActionStatus>;
  myOrderDetailsStatus: EActionStatus = EActionStatus.Pending;
  orders!: Order[];
  orderList!: IOrdersDetails | null;
  firstRowIndex = 0;
  pageNumberFromUrlParams = 0;
  pageSizeFromUrlParams = 0;
  pageNumber = 1;
  pageSize = 5;
  pageSizeOptions = [5, 10, 15];
  totalRecords!: number;
  activeOrderInfo = ECustomerOrganizationStatus.Active;
  maxSelectableDate: Date = new Date();
  sortField = 'created';
  sortOption = ESortOption.DESC;
  eSortOption = ESortOption;
  filtersApplied = false;
  isClearButtonPressed = false;
  dateFormat = DateTimeFormat;
  searchInProgress = false;
  clearInProgress = false;
  isManager = false;
  orderStatusEnum = OrderStatusEnum;

  physicianFilterOptions!: SelectItem[];
  filterByPhysiciansQuery?: string;
  filterByPhysiciansName?: SelectItem | null;
  selOrderStatusId: number | null = OptionAll.value as number | null;
  first: number = 0;
  rows: number = 5;
  private readonly unsubscribe$ = new Subject<void>();

  constructor() {
    this.myOrderDetailsStatus$ = this.store.select(selectOrderDetailsStatus);
  }

  ngOnInit(): void {
    this.populateQueryParams();
    this.store.dispatch(resetOrderFormDataFromMyOrdersPage());
    this.store.dispatch(loadOrderStatus());
    this.accountStatusSubscription();
    this.organizationIdSubscription();
    this.listOfOrderStatusSubscription();
    this.ordersSubscription();
    this.organizationChangeSubscription();
    this.customerRoleSubscription();
    this.getCustomersSubscription();
  }

  customerRoleSubscription() {
    const managerRoleId = Number(environment.managerRoleId);
    this.store
      .select(selectCustomerRole)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(userRole => {
          if (userRole) {
            this.isManager = Number(userRole) === managerRoleId;
          }
        })
      )
      .subscribe();
  }

  organizationIdSubscription() {
    this.store
      .select(selectCustomerOrganizationId)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(customerOrganizationId => {
          if (customerOrganizationId) {
            this.organizationId = customerOrganizationId;
          }
        })
      )
      .subscribe();
  }

  organizationChangeSubscription() {
    this.store
      .select(selectCustomerOrganization)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(customerOrganization => {
          if (customerOrganization) {
            this.orders = [];
            this.firstRowIndex = 0;
            const myOrderRequestModel = this.preparePayloadForGetOrdersData(this.hasFilters());
            this.getOrdersData(myOrderRequestModel);
            this.store.dispatch(loadOrdersOnOrganizationChange({isOrganizationChanged: false}));
          }
        })
      )
      .subscribe();
  }

  accountStatusSubscription() {
    this.accountStatus$ = this.store.select(selectCustomerOrganizationStatus).pipe(
      takeUntil(this.unsubscribe$),
      tap(status => {
        this.accountStatus = status;
      })
    );
  }

  ordersSubscription() {
    this.store
      .select(selectOrderDetails)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(orders => {
          if (orders) {
            this.orderList = orders;
            if (this.isManager) {
              this.orders =
                this.orderList?.listOfOrders?.filter(order => Number(order.orderStatusId) != EOrderStatus.New) ?? [];
            } else {
              this.orders = this.orderList?.listOfOrders ?? [];
            }
            this.totalRecords = this.orderList?.totalNumberOfResults ?? 0;
          }
          this.searchInProgress = false;
          this.clearInProgress = false;
        })
      )
      .subscribe();
  }

  listOfOrderStatusSubscription() {
    this.store
      .select(selectListOfOrderStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(orderStatus => {
          if (orderStatus) {
            this.listOfOrderStatus = orderStatus.map(status => {
              return {label: status.name, value: status.id};
            });
            this.listOfOrderStatus.unshift(OptionAll);
          }
        })
      )
      .subscribe();
  }

  clearInputFields() {
    this.patientNameInput = '';
    this.orderIdInput = null;
    this.dateRangeModel = null;
    this.selOrderStatusId = null;

    if (this.filterByPhysiciansName) {
      this.filterByPhysiciansName = null;
      this.filterByPhysiciansQuery = undefined;
    }
  }

  populateModelForSearch() {
    this.orderStatusModel = this.selOrderStatusId ?? null;
    this.patientNameModel = this.patientNameInput;
    this.orderIdModel = this.orderIdInput;
  }

  loadOrders(event: TableLazyLoadEvent, isSearch = false) {
    this.populateModelForSearch();

    if (this.hasFilters()) {
      this.filtersApplied = true;
    } else {
      this.filtersApplied = false;
    }
    this.firstRowIndex = isSearch ? 0 : this.first;
    this.pageSize = this.rows;
    this.pageNumber = this.firstRowIndex / this.pageSize + 1;
    this.sortField =
      ((Array.isArray(event.sortField) && event.sortField.length > 0
        ? event.sortField[0]
        : event.sortField) as string) ?? this.sortField;
    this.sortOption = event.sortOrder ?? this.sortOption;
    const myOrderRequestModel = this.preparePayloadForGetOrdersData(this.filtersApplied);
    this.getOrdersData(myOrderRequestModel);
  }

  onSearch(clicked: boolean) {
    this.searchInProgress = clicked;
    this.firstRowIndex = this.first = 0;
    this.loadOrders(
      {
        first: this.firstRowIndex,
        rows: this.pageSize,
        sortField: this.sortField,
        sortOrder: this.sortOption,
      } as TableLazyLoadEvent,
      true
    );
  }

  onClear() {
    this.clearInProgress = true;
    this.isClearButtonPressed = true;
    this.clearInputFields();
    this.clearFiltersFromUrl();
    this.onSearch(false);
  }

  hasFilters(): boolean {
    return (
      this.orderIdInput !== null ||
      this.patientNameInput !== '' ||
      this.dateRangeModel !== null ||
      this.selOrderStatusId !== null
    );
  }

  getOrderStatusName(statusId: number): string {
    const status = this.listOfOrderStatus?.find(option => option.value === statusId);
    return (status ? status.label : 'Unknown') as string;
  }

  getOrderStatusStyle(statusId: number): string {
    const status = this.listOfOrderStatus?.find(option => option.value === statusId);
    if (status) {
      if (status.value === OrderStatusEnum.InProgress) {
        return 'p-tag-info';
      } else if (status.value === OrderStatusEnum.Confirmed) {
        return 'p-tag-confirmed';
      } else if (status.value === OrderStatusEnum.Completed) {
        return 'p-tag-result';
      } else if (status.value === OrderStatusEnum.Cancelled) {
        return 'p-tag-inactive';
      } else if (status.value === OrderStatusEnum.New) {
        return 'p-tag-new';
      }
    }
    return 'p-tag-draft';
  }

  isEditable(orderStatusId: number): boolean {
    return orderStatusId === OrderStatusEnum.New;
  }

  getOrdersData(myOrderRequestModel: IMyOrderRequestModel) {
    if (this.accountStatus === ECustomerOrganizationStatus.Active) {
      this.store.dispatch(loadListOfOrders({request: myOrderRequestModel}));
    }
  }

  preparePayloadForGetOrdersData(filtersApplied: boolean): IMyOrderRequestModel {
    const request: IMyOrderRequestModel = {
      organizationId: this.organizationId ? this.organizationId : 0,
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sortField: this.sortField,
      sortOption: this.sortOption === ESortOption.ASC ? 'ASC' : 'DESC',
    };

    if (filtersApplied || this.isClearButtonPressed) {
      this.applyFilters(request);
      this.addFiltersToUrl(request);
    }

    return request;
  }

  addFiltersToUrl(request: IMyOrderRequestModel): void {
    const newRequest = new Map<string, string>();
    newRequest.set('first', this.first.toString());
    newRequest.set('rows', this.rows.toString());

    Object.entries(request).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        value !== 0 &&
        !(key === 'organizationId' || key === 'pageNumber' || key === 'pageSize')
      ) {
        newRequest.set(key, value.toString());
      }
    });

    this.router.navigate([], {
      queryParams: {
        ...Object.fromEntries(newRequest),
      },
      replaceUrl: true,
    });
  }

  clearFiltersFromUrl() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  applyFilters(request: IMyOrderRequestModel): void {
    request.orderId = this.orderIdModel ? this.orderIdModel : 0;
    request.patientName = this.patientNameModel;
    request.physicianName = this.filterByPhysiciansName?.label ?? this.filterByPhysiciansQuery;
    request.orderStatusId = this.orderStatusModel ? this.orderStatusModel : 0;
    request.orderStartDate = this.dateRangeModel
      ? this.convertToStartOfDayUTCString(this.dateRangeModel[0])
      : undefined;
    request.orderEndDate = this.getOrderEndDate();
  }

  getOrderEndDate(): string | undefined {
    if (!this.dateRangeModel) {
      return undefined;
    }
    const endDate = this.dateRangeModel[1] ?? new Date();
    return this.convertToEndOfDayUTCString(endDate);
  }

  convertToStartOfDayUTCString(date: Date): string {
    return dayjs(date).startOf('day').utcOffset(0, true).toISOString();
  }

  convertToEndOfDayUTCString(date: Date): string {
    return dayjs(date).endOf('day').utcOffset(0, true).toISOString();
  }

  editOrder(orderInfo: Order): void {
    this.router.navigate(['/checkout'], {
      queryParams: {id: orderInfo.id},
    });
  }

  viewOrder(orderInfo: Order): void {
    this.router.navigate([`/my-orders/order-details`], {
      queryParams: {
        orderId: orderInfo.id,
      },
    });
  }

  onPhysicianAutoComplete(autoCompleteEvent?: AutoCompleteCompleteEvent) {
    if (autoCompleteEvent) {
      const query = autoCompleteEvent.query;
      this.filterByPhysiciansQuery = query;
      if (query && query.length >= environment.minAutoCompleteLength) {
        this.store.dispatch(
          customersAction({
            request: {
              name: query,
              organizationId: this.organizationId,
            } as ICustomerRequest,
          })
        );
      } else {
        this.physicianFilterOptions = [];
      }
    } else {
      this.filterByPhysiciansQuery = undefined;
      this.physicianFilterOptions = [];
    }
  }

  onPhysicianSelection(item: SelectItem) {
    this.filterByPhysiciansName = item.value;
  }

  private getCustomersSubscription() {
    this.store
      .select(selectCustomers)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((state: ICustomersState) => {
          if (state.successResponse) {
            this.physicianFilterOptions = state.successResponse.listOfCustomers
              ?.map(customer => `${customer.firstName} ${customer.lastName}`)
              .filter((value, index, array) => array.indexOf(value) === index)
              .map(label => ({label: label}) as SelectItem);
          }
        })
      )
      .subscribe();
  }

  navigateToNewOrder(): void {
    this.router.navigate(['/category']);
  }

  populateQueryParams() {
    this.route.queryParams.subscribe(params => {
      this.patientNameInput = params['patientName'] ?? null;
      this.orderIdInput = params['orderId'] ?? null;
      if (params['orderStatusId']) this.selOrderStatusId = Number(params['orderStatusId']);

      if (params['pageNumber']) this.pageNumberFromUrlParams = Number(params['pageNumber']);
      if (params['pageSize']) this.pageSizeFromUrlParams = Number(params['pageSize']);

      if (params['orderStartDate']) {
        this.dateRangeModel = [new Date(params['orderStartDate'].split('T')[0]), null];
      }
      if (params['orderEndDate'] && this.dateRangeModel) {
        this.dateRangeModel[1] = new Date(params['orderEndDate'].split('T')[0]);
      }
      if (params['first']) this.first = Number(params['first']);
      if (params['rows']) this.rows = Number(params['rows']);
      if (params['sortField']) this.sortField = params['sortField'];
      if (params['sortOption']) {
        this.sortOption = params['sortOption'] === 'ASC' ? ESortOption.ASC : ESortOption.DESC;
      }
    });
  }
  onPageChange(event: {first: number; rows: number}) {
    this.first = event.first;
    this.rows = event.rows;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
